import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Row, Col, Tag, message } from 'antd';
import axios from 'axios';
import moment from 'moment';

const OrderManagementModal = ({ visible, onCancel, patientId, onOrdersUpdate }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);



  // Fetch orders when modal opens
  useEffect(() => {
    if (visible && patientId) {
      fetchOrders();
    }
  }, [visible, patientId]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/patients/${patientId}/orders`);
      // Filter to only show pending or active orders
      const filteredOrders = (response.data.data.orders || []).filter(order =>
        order.status === 'Pending' || order.status === 'Active'
      );
      setOrders(filteredOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = (order) => {
    setViewingOrder(order);
    setIsViewModalVisible(true);
  };



  // Helper function to get display name for orders
  const getOrderDisplayName = (order) => {
    if (!order) return 'Unnamed Order';

    const { orderType, orderDetails, medication, labTemplate } = order;

    // Return appropriate display name based on order type and available fields
    switch (orderType) {
      case 'Laboratory':
        // Check for populated labTemplate name first, then orderDetails.testName
        return labTemplate?.templateName || orderDetails?.testName || orderDetails?.description || 'Laboratory Order';
      case 'Radiology':
        return orderDetails?.studyDescription || orderDetails?.description || 'Radiology Order';
      case 'Medication':
        // Check for populated medication name first, then orderDetails.medicationName
        return medication?.name || orderDetails?.medicationName || orderDetails?.description || 'Medication Order';
      case 'Lifestyle':
        return orderDetails?.title || orderDetails?.description || 'Lifestyle Order';
      case 'Referral':
        return orderDetails?.specialty ? `${orderDetails.specialty} Referral` :
               orderDetails?.description || 'Referral Order';
      case 'Procedure':
        return orderDetails?.description || 'Procedure Order';
      case 'Nursing':
        return orderDetails?.description || 'Nursing Order';
      case 'Vaccine':
        return orderDetails?.description || 'Vaccine Order';
      case 'Preventive Screening':
        return orderDetails?.description || 'Preventive Screening Order';
      default:
        // Comprehensive fallback logic
        return medication?.name ||
               labTemplate?.templateName ||
               orderDetails?.description ||
               orderDetails?.testName ||
               orderDetails?.studyDescription ||
               orderDetails?.medicationName ||
               orderDetails?.title ||
               `${orderType} Order`;
    }
  };

  const columns = [
    {
      title: 'Order Name',
      dataIndex: 'orderName',
      key: 'orderName',
      width: '25%',
      render: (text, record) => <strong>{getOrderDisplayName(record)}</strong>
    },
    {
      title: 'Type',
      dataIndex: 'orderType',
      key: 'orderType',
      width: '12%',
      render: (type) => <Tag color="blue">{type}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '12%',
      render: (status) => {
        const color = status === 'Pending' ? 'orange' :
                     status === 'In Progress' ? 'blue' :
                     status === 'Completed' ? 'green' :
                     status === 'Cancelled' ? 'red' : 'default';
        return <Tag color={color}>{status}</Tag>;
      }
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: '10%',
      render: (priority) => {
        const color = priority === 'Low' ? 'default' :
                     priority === 'Normal' ? 'blue' :
                     priority === 'High' ? 'orange' :
                     priority === 'Urgent' ? 'red' : 'default';
        return <Tag color={color}>{priority}</Tag>;
      }
    },
    {
      title: 'Date Ordered',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: '15%',
      render: (date) => date ? moment(date).format('DD/MM/YYYY') : 'N/A',
      sorter: (a, b) => moment(a.orderDate) - moment(b.orderDate)
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => handleViewOrder(record)}
        >
          View
        </Button>
      )
    }
  ];

  return (
    <>
      <Modal
        title="Order Management - Active & Pending Orders"
        visible={visible}
        onCancel={onCancel}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} orders`
          }}
          scroll={{ x: 800 }}
        />
      </Modal>

      {/* View Order Modal */}
      <Modal
        title="Order Details"
        visible={isViewModalVisible}
        onCancel={() => {
          setIsViewModalVisible(false);
          setViewingOrder(null);
        }}
        footer={null}
        destroyOnClose
        width={600}
      >
        {viewingOrder && (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <strong>Order Name:</strong>
                <div>{getOrderDisplayName(viewingOrder)}</div>
              </Col>
              <Col span={12}>
                <strong>Order Type:</strong>
                <div>{viewingOrder.orderType}</div>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <strong>Status:</strong>
                <div>
                  <Tag color={viewingOrder.status === 'Active' ? 'green' : 'orange'}>
                    {viewingOrder.status}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <strong>Priority:</strong>
                <div>
                  <Tag color={
                    viewingOrder.priority === 'High' ? 'red' :
                    viewingOrder.priority === 'Medium' ? 'orange' : 'blue'
                  }>
                    {viewingOrder.priority}
                  </Tag>
                </div>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <strong>Date Ordered:</strong>
                <div>{viewingOrder.orderDate ? moment(viewingOrder.orderDate).format('DD/MM/YYYY') : 'N/A'}</div>
              </Col>
              <Col span={12}>
                <strong>Ordering Provider:</strong>
                <div>{viewingOrder.orderingProvider?.name || 'N/A'}</div>
              </Col>
            </Row>

            {viewingOrder.notes && (
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={24}>
                  <strong>Notes:</strong>
                  <div style={{ marginTop: 8, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                    {viewingOrder.notes}
                  </div>
                </Col>
              </Row>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default OrderManagementModal;
